import { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    IncomingVehicleInLoadPreview,
    OutgoingVehicleInLoadPreview,
    VehicleInUnannouncedLoadPreview,
} from '../../compoundManagement.types';
import { DisplayModelName } from '../dataDisplay/DisplayModelName';
import { DisplayDispatchType } from '../dataDisplay/DisplayDispatchType';
import { NoValue } from '../../../sharedComponents/NoValue';
import Button from '@rio-cloud/rio-uikit/Button';
import { DisplayMeansOfTransportType } from '../dataDisplay/DisplayMeansOfTransportType';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import { CopyToClipboard } from '../../../sharedComponents/CopyToClipboard';

type BulkStatusUpdateReviewTableProps = {
    vehicles: IncomingVehicleInLoadPreview[] | VehicleInUnannouncedLoadPreview[] | OutgoingVehicleInLoadPreview[];
    onRemove: (vehicleId: string) => void;
};

export const BulkStatusUpdateReviewTable: FunctionComponent<BulkStatusUpdateReviewTableProps> = (props) => {
    return props.vehicles.length === 0 ? <EmptyReviewTable /> : <ReviewTable {...props} />;
};

const EmptyReviewTable = () => {
    return (
        <div className="display-flex align-items-center margin-bottom-20 justify-content-center">
            <span className="rioglyph rioglyph-info-sign text-size-20 margin-right-10" />
            <span className="text-light">
                <FormattedMessage id="outboundOrderBook.compoundManagement.bulkStatusUpdate.table.empty" />
            </span>
        </div>
    );
};

export const isVehicleWithArrival = (
    vehicle: IncomingVehicleInLoadPreview | VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview,
): vehicle is IncomingVehicleInLoadPreview => vehicle !== undefined && 'arrival' in vehicle;

export const isVehicleWithDeparture = (
    vehicle: IncomingVehicleInLoadPreview | VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview,
): vehicle is OutgoingVehicleInLoadPreview => vehicle !== undefined && 'departure' in vehicle;

const ReviewTable: FunctionComponent<BulkStatusUpdateReviewTableProps> = ({ vehicles, onRemove }) => {
    const hasArrival = isVehicleWithArrival(vehicles[0]);
    const hasDeparture = isVehicleWithDeparture(vehicles[0]);

    return (
        <>
            <div className="text-center margin-bottom-20">
                <span className="text-light">
                    <FormattedMessage
                        id="outboundOrderBook.compoundManagement.bulkStatusUpdate.table.title"
                        values={{ count: vehicles.length }}
                    />
                </span>
            </div>
            <div className="height-300 overflow-y-auto">
                <table className="table table-column-overflow-hidden table-sticky-in-container table-hover table-head-filled margin-bottom-20">
                    <thead>
                        <ReviewTableHeader hasTransportInformation={hasArrival || hasDeparture} />
                    </thead>
                    <tbody>
                        {vehicles.map((vehicle) => (
                            <ReviewTableRow key={vehicle.id} vehicle={vehicle} onRemove={onRemove} />
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const ReviewTableHeader = (props: { hasTransportInformation: boolean }) => (
    <tr>
        <th>
            <FormattedMessage id="outboundOrderBook.common.heading.vin" />
        </th>
        <th>
            <FormattedMessage id="outboundOrderBook.common.heading.manufacturer" />
        </th>
        <th>
            <FormattedMessage id="outboundOrderBook.common.heading.model" />
        </th>
        <th>
            <FormattedMessage id="outboundOrderBook.common.heading.vehicleType" />
        </th>
        <th>
            <FormattedMessage id="outboundOrderBook.compoundManagement.table.heading.modelVersion" />
        </th>
        <th>
            <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.finalDestination" />
        </th>
        {props.hasTransportInformation && (
            <>
                <th>
                    <FormattedMessage id="outboundOrderBook.common.heading.meansOfTransportType" />
                </th>
                <th>
                    <FormattedMessage id="outboundOrderBook.common.heading.meansOfTransport" />
                </th>
            </>
        )}
        <th className="text-right ">
            <FormattedMessage id="outboundOrderBook.common.heading.removeAction" />
        </th>
    </tr>
);

type ReviewTableRowProps = {
    vehicle: VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview;
    onRemove: (vehicleId: string) => void;
};

const ReviewTableRow: FunctionComponent<ReviewTableRowProps> = ({ vehicle, onRemove }: ReviewTableRowProps) => {
    const intl = useIntl();
    const spanOrNoValue = (text: string | undefined) => (text ? <span>{text}</span> : <NoValue />);

    const finalDestination = vehicle.vehicle.finalDestination;

    const transportInformation = isVehicleWithArrival(vehicle)
        ? vehicle.arrival.transportInformation
        : isVehicleWithDeparture(vehicle)
          ? vehicle.departure.transportInformation
          : undefined;

    const removeLabel = intl.formatMessage({
        id: 'outboundOrderBook.compoundManagement.bulkStatusUpdate.table.remove',
    });
    return (
        <tr>
            <td>
                <CopyToClipboard value={vehicle.vehicle.vin} />
            </td>
            <td>{spanOrNoValue(vehicle.vehicle.model.manufacturer)}</td>
            <td>
                <DisplayModelName vehicle={vehicle.vehicle} />
            </td>
            <td>
                <DisplayDispatchType vehicle={vehicle.vehicle} />
            </td>
            <td>{spanOrNoValue(vehicle.vehicle.model.version)}</td>
            <td>{spanOrNoValue(finalDestination?.identifier ?? finalDestination?.address?.countryCode)}</td>
            {transportInformation && (
                <>
                    <td>
                        <DisplayMeansOfTransportType meansOfTransportType={transportInformation.meansOfTransportType} />
                    </td>
                    <td>{spanOrNoValue(transportInformation.meansOfTransportId)}</td>
                </>
            )}
            <td className="text-right">
                <OverlayTrigger overlay={<Tooltip>{removeLabel}</Tooltip>}>
                    <Button
                        type="button"
                        iconOnly
                        iconName="rioglyph-trash"
                        bsStyle={Button.DANGER}
                        variant={Button.VARIANT_LINK}
                        onClick={() => onRemove(vehicle.id)}
                        aria-label={removeLabel}
                    />
                </OverlayTrigger>
            </td>
        </tr>
    );
};
